import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { actionTemplates } from '../../../../../api/coreData'
import Loading from '../../../../molecules/Loading'
import AdminPage from '../../../admin/shared/AdminPage'
import ErrorDisplay from '../../../../molecules/ErrorDisplay'
import SydTabs from '../../../../commonDesign/SydTabs'
import SnackAlert from '../../../../molecules/SnackAlert/SnackAlert'
import DefinitionTab from './DefinitionTab'
import PreviewTab from './PreviewTab'
import VersionHistoryTab from './VersionHistoryTab'
import SettingsTab from './SettingsTab'
import NewVersionDialog from './NewVersionDialog'
import SetActiveVersionDialog from './SetActiveVersionDialog'
import { normalizeDefinition } from './utils'

const tabs = {
  edit: { label: 'Edit', value: 'edit' },
  preview: { label: 'Preview', value: 'preview' },
  versions: { label: 'Versions', value: 'versions' },
  settings: { label: 'Settings', value: 'settings' }
}

const useStyles = makeStyles(() => ({
  pageHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '4px'
  },
  templateName: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 700,
    lineHeight: 1.3
  },
  templateMeta: {
    fontSize: 'var(--font-size-xs)',
    marginTop: '2px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  metaCode: {
    fontWeight: 600,
    fontFamily: 'monospace',
    fontSize: '11px'
  },
  metaSep: {
    color: 'var(--color-neutral-300)'
  },
  metaVersion: {
    color: 'var(--text-color-subtle)',
    fontFamily: 'monospace',
    fontSize: '11px'
  },
  metaDanger: {
    color: 'var(--color-danger-400)',
    fontSize: '11px'
  },
  tabBar: {
    marginBottom: '16px'
  },
  tabContent: {
    minHeight: '400px'
  }
}))

function ActionTemplateDetails () {
  const classes = useStyles()
  const { actionTemplateId } = useParams()
  const templateId = parseInt(actionTemplateId, 10)
  const addDefinitionRef = useRef(null)
  const setActiveVersionRef = useRef(null)
  const [toast, setToast] = useState(null)
  const [workingDefinition, setWorkingDefinition] = useState(null)

  const showToast = useCallback((message, severity = 'success') => {
    setToast({ openAlert: true, alertMessage: message, alertSeverity: severity })
  }, [])

  useEffect(() => {
    setWorkingDefinition(null)
  }, [templateId])

  const validId = !isNaN(templateId)

  const { data: template, isLoading, error, refetch } = actionTemplates.useGetActionTemplate(
    validId ? templateId : null,
    { includes: { definition: true } }
  )

  const { data: versions, refetch: refetchVersions } = actionTemplates.useListActionTemplateVersions(
    validId ? templateId : null
  )

  const previewTemplate = useMemo(() => {
    if (!template) return null
    const def = workingDefinition || normalizeDefinition(template?.definition)
    return {
      ...template,
      definition: { ...def, dataPipelineCode: null }
    }
  }, [template, workingDefinition])

  const handleAddDefinition = useCallback((initialDefinition) => {
    addDefinitionRef.current.open(initialDefinition)
  }, [])

  const handleSetActiveVersion = useCallback((version) => {
    setActiveVersionRef.current.open(version)
  }, [])

  const handleDefinitionComplete = useCallback(() => {
    setWorkingDefinition(null)
    refetch()
    refetchVersions()
    showToast('New version saved and set as active')
  }, [refetch, refetchVersions, showToast])

  const handleSetActiveComplete = useCallback(() => {
    refetch()
    refetchVersions()
    showToast('Active version updated')
  }, [refetch, refetchVersions, showToast])

  if (!validId || error) {
    return (
      <AdminPage>
        <ErrorDisplay error={error || 'Invalid action template ID'} onRetry={validId ? refetch : undefined} />
      </AdminPage>
    )
  }

  if (isLoading || !template) {
    return (
      <AdminPage>
        <Loading />
      </AdminPage>
    )
  }

  return (
    <AdminPage>
      <SydTabs.Group
        tabOptions={tabs}
        defaultValue='edit'
        key={templateId}
      >
        <div className={classes.pageHeader}>
          <div>
            <h2 className={classes.templateName}>{template.name}</h2>
            <div className={classes.templateMeta}>
              <span className={classes.metaCode}>{template.code}</span>
              {template.definitionVersionId ? (
                <>
                  <span className={classes.metaSep}>/</span>
                  <span className={classes.metaVersion}>{template.definitionVersionId}</span>
                </>
              ) : (
                <span className={classes.metaDanger}>No active definition</span>
              )}
            </div>
          </div>
        </div>

        <div className={classes.tabBar}>
          <SydTabs.List />
        </div>

        <div className={classes.tabContent}>
          <SydTabs.Tab value='edit'>
            <DefinitionTab template={template} onSave={handleAddDefinition} onDefinitionChange={setWorkingDefinition} initialDefinition={workingDefinition} />
          </SydTabs.Tab>
          <SydTabs.Tab value='preview'>
            <PreviewTab template={previewTemplate} />
          </SydTabs.Tab>
          <SydTabs.Tab value='versions'>
            <VersionHistoryTab
              template={template}
              versions={versions || []}
              onSetActive={handleSetActiveVersion}
            />
          </SydTabs.Tab>
          <SydTabs.Tab value='settings'>
            <SettingsTab template={template} onSave={refetch} onToast={showToast} />
          </SydTabs.Tab>
        </div>
      </SydTabs.Group>

      <NewVersionDialog
        key={`new-version-${templateId}`}
        ref={addDefinitionRef}
        template={template}
        onComplete={handleDefinitionComplete}
      />
      <SetActiveVersionDialog
        key={`set-active-${templateId}`}
        ref={setActiveVersionRef}
        template={template}
        onComplete={handleSetActiveComplete}
      />
      <SnackAlert alert={toast} />
    </AdminPage>
  )
}

export default ActionTemplateDetails
